// School Calendar — the shared term calendar (Module B). The spine, and editable: it changes with
// government/school schedule updates, so any signed-in member can add / move / remove an entry.
// Team-tier; calm, factual. A removed entry is RECOVERABLE (soft-delete) — never a red alarm.

import { api } from "../api";
import { esc } from "../dom";

type EntryType = "teaching" | "test" | "event" | "holiday";
interface Entry {
  id: string;
  entry_date: string; // YYYY-MM-DD
  label: string;
  type: EntryType;
  week_no?: number;
}
interface CalResponse {
  term: { name: string } | null;
  entries: Entry[];
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TYPES: EntryType[] = ["teaching", "test", "event", "holiday"];

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const wd = WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd} ${d} ${MO[m - 1]}`;
}
function typeOptions(selected: EntryType): string {
  return TYPES.map((t) => `<option value="${t}"${t === selected ? " selected" : ""}>${t}</option>`).join("");
}

export async function renderCalendar(app: HTMLElement): Promise<void> {
  // Page-local state (lives in this closure; reset when the page is re-entered).
  let editingId: string | null = null;
  let undo: { id: string; label: string } | null = null;
  let err: string | null = null;
  let state = { upcoming: emptyCal(), term: emptyCal(), deleted: emptyCal() };

  function emptyCal(): CalResponse {
    return { term: null, entries: [] };
  }

  async function refresh(): Promise<void> {
    const [upcoming, term, deleted] = await Promise.all([
      api<CalResponse>("/api/calendar?scope=upcoming"),
      api<CalResponse>("/api/calendar?scope=term"),
      api<CalResponse>("/api/calendar?scope=deleted"),
    ]);
    state = { upcoming, term, deleted };
  }

  // ── row renderers ────────────────────────────────────────────────────────────
  const readonlyRow = (e: Entry): string =>
    `<div class="cal-row"><span class="cal-date">${esc(fmtDate(e.entry_date))}</span>` +
    `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}">${e.type}</span></div>`;

  const editableRow = (e: Entry): string =>
    e.id === editingId ? editFormRow(e) : staticRow(e);

  const staticRow = (e: Entry): string =>
    `<div class="cal-row"><span class="cal-date">${esc(fmtDate(e.entry_date))}</span>` +
    `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}">${e.type}</span>` +
    `<button class="linkbtn" type="button" data-act="edit" data-id="${esc(e.id)}" aria-label="Edit ${esc(e.label)}">Edit</button></div>`;

  const editFormRow = (e: Entry): string =>
    `<form class="cal-edit" data-id="${esc(e.id)}">
      <input type="date" name="entry_date" value="${esc(e.entry_date)}" required aria-label="Date" />
      <input name="label" value="${esc(e.label)}" required aria-label="Label" />
      <select name="type" aria-label="Type">${typeOptions(e.type)}</select>
      <div class="row-actions">
        <button class="btn sm" type="submit">Save</button>
        <button class="btn sm secondary" type="button" data-act="cancel">Cancel</button>
        <button class="linkbtn quiet" type="button" data-act="remove" data-id="${esc(e.id)}">Remove</button>
      </div>
    </form>`;

  const removedRow = (e: Entry): string =>
    `<div class="cal-row removed"><span class="cal-date">${esc(fmtDate(e.entry_date))}</span>` +
    `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}">${e.type}</span>` +
    `<button class="linkbtn" type="button" data-act="restore" data-id="${esc(e.id)}">Restore</button></div>`;

  // ── page body ────────────────────────────────────────────────────────────────
  function body(): string {
    const up = state.upcoming.entries;
    const upcomingHtml = up.length ? up.map(readonlyRow).join("") : `<p class="empty">Nothing coming up.</p>`;

    // Full term grouped by week (these rows are editable).
    const byWeek = new Map<number, Entry[]>();
    for (const e of state.term.entries) {
      const w = e.week_no ?? 0;
      (byWeek.get(w) ?? byWeek.set(w, []).get(w)!).push(e);
    }
    const weekLabel = (w: number): string => (w <= 0 ? "Before term" : `Week ${w}`);
    const termHtml = [...byWeek.keys()]
      .sort((a, b) => a - b)
      .map((w) => `<h2>${weekLabel(w)}</h2>${byWeek.get(w)!.map(editableRow).join("")}`)
      .join("");

    const undoHtml = undo
      ? `<div class="toast" id="undotoast">Removed “${esc(undo.label)}”. <button class="linkbtn" type="button" data-act="undo" data-id="${esc(undo.id)}">Undo</button></div>`
      : "";
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";

    const removed = state.deleted.entries;
    const removedHtml = removed.length
      ? `<h2>Recently removed</h2><p class="hint">Removed entries are kept and can be brought back.</p>${removed.map(removedRow).join("")}`
      : "";

    return `
      ${errHtml}
      ${undoHtml}

      <h2>Upcoming</h2>
      ${upcomingHtml}

      ${state.term.entries.length ? termHtml : ""}

      <h2>Add to the calendar</h2>
      <form id="addentry">
        <label for="cal-date">Date</label>
        <input id="cal-date" name="entry_date" type="date" required />
        <label for="cal-label">What is it?</label>
        <input id="cal-label" name="label" placeholder="e.g. Khmer New Year holiday" required />
        <label for="cal-type">Type</label>
        <select id="cal-type" name="type">${typeOptions("event")}</select>
        <div style="height:12px"></div>
        <button class="btn" type="submit">Add entry</button>
      </form>

      ${removedHtml}`;
  }

  function render(): void {
    app.innerHTML = `
      <div class="topbar"><a data-link href="/" class="muted">← Home</a><a data-link href="/calendar/import" class="muted">Import</a></div>
      <h1>School Calendar</h1>
      <p class="hint">${esc(state.term.term?.name ?? "")}</p>
      <div id="calroot">${body()}</div>`;

    // Fresh container each render → delegated listeners never accumulate across navigations.
    const root = app.querySelector<HTMLElement>("#calroot")!;
    root.addEventListener("click", onClick);
    root.addEventListener("submit", onSubmit);
  }

  // ── actions (each catches errors and shows a calm message) ─────────────────────
  async function mutate(run: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await run();
      await refresh();
    } catch (e) {
      err = (e as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function onClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!btn) return;
    const act = btn.dataset.act!;
    const id = btn.dataset.id ?? null;

    if (act === "edit") { editingId = id; undo = null; render(); return; }
    if (act === "cancel") { editingId = null; render(); return; }
    if (act === "remove" && id) {
      const label = state.term.entries.find((x) => x.id === id)?.label ?? "Entry";
      void mutate(async () => {
        await api(`/api/calendar/${id}`, { method: "DELETE" });
        editingId = null;
        undo = { id, label };
      });
      return;
    }
    if ((act === "undo" || act === "restore") && id) {
      void mutate(async () => {
        await api(`/api/calendar/${id}/restore`, { method: "POST" });
        undo = null;
      });
      return;
    }
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    e.preventDefault();

    if (form.id === "addentry") {
      const fd = new FormData(form);
      const entry_date = String(fd.get("entry_date") ?? "");
      const label = String(fd.get("label") ?? "").trim();
      const type = String(fd.get("type") ?? "event");
      if (!entry_date || !label) return;
      void mutate(async () => {
        await api("/api/calendar", { method: "POST", body: JSON.stringify({ entry_date, label, type }) });
      });
      return;
    }

    if (form.classList.contains("cal-edit")) {
      const id = form.dataset.id!;
      const fd = new FormData(form);
      const entry_date = String(fd.get("entry_date") ?? "");
      const label = String(fd.get("label") ?? "").trim();
      const type = String(fd.get("type") ?? "");
      if (!entry_date || !label) return;
      void mutate(async () => {
        await api(`/api/calendar/${id}`, { method: "PATCH", body: JSON.stringify({ entry_date, label, type }) });
        editingId = null;
      });
      return;
    }
  }

  await refresh();
  render();
}
